import { colors } from "@indeed/ifl-tokens";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { StyleSheet, useWindowDimensions } from "react-native";

import { Flex, Box, Button, Text, Bookmark } from "../../../../../../mads";
import { useI18n } from "../../../../../../shared/I18n";
import { logScreenView } from "../../../../../../shared/native-modules/tare/logScreenView";
import { logTapButton } from "../../../../../../shared/native-modules/tare/logTapButton";
import openLoginModal from "../../../../../../shared/native-modules/tare/openLoginModal";
import type { NativeContextRNProps } from "../../../../../../shared/props/NativeContextRNProps";
import { useViewjobSourceUrl } from "../../../../context/ViewJobDataContext";
import {
  FeedbackType,
  hasJobFeedback,
  markJobWithFeedback,
} from "../../../../services/JobFeedbackStorage";
import { useSaveJobButtonQuery } from "../SaveJobButton/__generated__/SaveJobButton.gqlgenerated";
import useSaveJob from "../SaveJobButton/useToggleSaveJob";

export interface JobFeedbackPromptProps {
  jobKey: string;
  nativeContext?: NativeContextRNProps;
  onSave?: () => void;
  onNotInterested?: () => void;
  onShown?: () => void;
  isLoggedIn: boolean;
  reload: () => void;
}

export function JobFeedbackPrompt({
  jobKey,
  onSave,
  onNotInterested,
  onShown,
  isLoggedIn,
  reload,
}: JobFeedbackPromptProps) {
  const { gettext } = useI18n();
  const sourceUrl = useViewjobSourceUrl();
  const [isLoading, setIsLoading] = useState(true);
  const [hasFeedback, setHasFeedback] = useState(true);
  const { data } = useSaveJobButtonQuery({
    variables: {
      jobKey,
    },
  });
  const savedJobState = data?.jobData?.results?.[0]?.job?.savedJob?.state;
  const isJobSaved = savedJobState ? savedJobState !== "VISITED" : false;
  const isFromAppShare = useMemo(() => {
    if (!sourceUrl) {
      return false;
    }
    try {
      const urlObj = new URL(sourceUrl);
      const fromParam = urlObj.searchParams.get("from");
      return (fromParam && fromParam.startsWith("appshare")) || false;
    } catch (error) {
      console.error("Error parsing source URL:", error);
      return false;
    }
  }, [sourceUrl]);

  // Check if the job has received feedback (saved or marked as not now)
  useEffect(() => {
    let isMounted = true;
    const checkFeedbackStatus = async () => {
      const feedbackExists = await hasJobFeedback(jobKey);
      if (isMounted) {
        setHasFeedback(feedbackExists);
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- FIXME
    checkFeedbackStatus().finally(() => {
      if (isMounted) {
        setIsLoading(false);
      }
    });
    return () => {
      isMounted = false;
    };
  }, [jobKey]);

  useEffect(() => {
    if (!isLoading && !hasFeedback && isFromAppShare && !isJobSaved) {
      onShown?.();
    }
  }, [hasFeedback, isFromAppShare, isJobSaved, isLoading, onShown]);

  const screenWidth = useWindowDimensions();
  const SMALL_SCREEN_THRESHOLD = 320;
  const isSmallScreen = screenWidth.width < SMALL_SCREEN_THRESHOLD;
  const { toggle } = useSaveJob(jobKey);

  // Handle save job button click
  const handleSavePress = useCallback(async () => {
    // If the user is not logged in, prompt them to log in
    if (!isLoggedIn) {
      openLoginModal(false, "viewjob")
        .then(() => {
          logScreenView("rn-view-job-feedback-prompt-logged-in").catch(console.error);
          reload();
        })
        .catch(() => {
          logScreenView("rn-view-job-feedback-prompt-login-cancelled").catch(console.error);
        });
      logTapButton("rn-view-job-feedback-prompt", "open-login-modal").catch(console.error);
      return;
    } else {
      // Update job state to SAVED
      await toggle("VISITED");
      await markJobWithFeedback(jobKey, FeedbackType.SAVED);
      setHasFeedback(true);
      logTapButton("rn-view-job-feedback-prompt", "save").catch(console.error);
      onSave?.();
    }
  }, [isLoggedIn, reload, toggle, jobKey, onSave]);

  // Handle "Not now" button click
  const handleNotInterestedPress = useCallback(async () => {
    // Mark job as having received feedback to prevent showing the prompt again
    await markJobWithFeedback(jobKey, FeedbackType.NOT_NOW);
    setHasFeedback(true);

    logTapButton("rn-view-job-feedback-prompt", "not-now").catch(console.error);
    onNotInterested?.();
  }, [jobKey, onNotInterested]);

  if (isLoading) {
    return <Box sx={[styles.container, styles.placeholder]} />;
  }

  if (hasFeedback || isJobSaved || !isFromAppShare) {
    return null;
  }

  return (
    <Box sx={styles.container}>
      <Text level={3} variant="normal" sx={styles.title}>
        {gettext("Save suggestion to My Jobs?")}
      </Text>
      <Flex sx={isSmallScreen ? styles.buttonsContainerSmall : styles.buttonsContainer}>
        <Box sx={styles.buttonBox}>
          <Button
            variant="inverse"
            size="md"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises -- FIXME
            onPress={handleNotInterestedPress}
          >
            {gettext("Not now")}
          </Button>
        </Box>
        <Box sx={styles.buttonBox}>
          <Button
            variant="primary"
            size="md"
            iconAfter={
              <Bookmark variant={"stroke"} size="md" color={colors.foreground.inverse["[base]"]} />
            }
            // eslint-disable-next-line @typescript-eslint/no-misused-promises -- FIXME
            onPress={handleSavePress}
          >
            {gettext("Save")}
          </Button>
        </Box>
      </Flex>
    </Box>
  );
}

const styles = StyleSheet.create({
  container: {
    borderRadius: 20,
    backgroundColor: colors.background.inverse["[base]"],
    elevation: 0,
    padding: 16,
    alignItems: "center",
  },
  placeholder: {
    opacity: 0,
    backgroundColor: "transparent",
    minHeight: 300,
    pointerEvents: "none",
  },
  title: {
    fontWeight: "700",
    marginBottom: 16,
    color: colors.foreground.inverse["[base]"],
  },
  buttonsContainer: {
    flexDirection: "row",
    width: "100%",
    gap: 8,
  },
  buttonsContainerSmall: {
    flexDirection: "column",
    width: "100%",
    gap: 8,
  },
  buttonBox: {
    flex: 1,
  },
});
